import { useEffect, useRef, useState } from "react"
import {
	IconLink3Outline18 as Link3,
	IconSquareArrowUpRightOutline18 as OpenExternal,
	IconTrashOutline18 as Trash,
} from "nucleo-ui-outline-18"

import type { HelpCenterArticle } from "@productlane/zero"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { Button, IconButton, Input, Shortcut } from "@productlane/ui"
import {
	DropdownMenu,
	DropdownMenuItem,
} from "@productlane/ui/src/components/internal/dropdown"
import { EllipsisVerticalIcon as MoreVertical } from "@productlane/ui/src/icons/project/ellipsis-icon"
import { mutators } from "@productlane/zero/mutators"

import { RoleCheck } from "@/components/shared/role-check"
import { toast } from "@/components/toast"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero } from "@/lib/zero"
import { MobileBackButton } from "../mobile-back-button"
import { normalizeExternalUrl } from "./article-kind"
import { DocsHeaderTabs } from "./docs-header-tabs"
import { LinkPreviewCard } from "./link-preview-card"
import { useArticleDelete } from "./use-article-delete"

const SAVE_DEBOUNCE_MS = 600

export const LinkArticleView = ({
	article,
}: {
	article: HelpCenterArticle
}) => {
	const zero = useZero()
	const { role } = useRole()
	const canEdit = role !== "VIEWER"

	const [title, setTitle] = useState(article.title)
	const [url, setUrl] = useState(article.externalUrl ?? "")
	const timer = useRef<number | null>(null)

	useEffect(() => {
		setTitle(article.title)
		setUrl(article.externalUrl ?? "")
	}, [article.id, article.title, article.externalUrl])

	useEffect(
		() => () => {
			if (timer.current) window.clearTimeout(timer.current)
		},
		[],
	)

	const save = (values: { title?: string; externalUrl?: string | null }) =>
		mutateOk(
			zero.mutate(
				mutators.helpCenterArticles.update({ id: article.id, ...values }),
			),
		)

	const queueSave = (values: {
		title?: string
		externalUrl?: string | null
	}) => {
		if (timer.current) window.clearTimeout(timer.current)
		timer.current = window.setTimeout(() => void save(values), SAVE_DEBOUNCE_MS)
	}

	const commitUrl = () => {
		if (timer.current) window.clearTimeout(timer.current)
		const trimmed = url.trim()
		if (!trimmed) {
			void save({ externalUrl: null })
			return
		}
		const normalized = normalizeExternalUrl(trimmed)
		if (!normalized) {
			toast.error({
				icon: Link3,
				title: `"${trimmed}" isn't a web address`,
				description: "Enter a link starting with https://",
			})
			return
		}
		setUrl(normalized)
		void save({ externalUrl: normalized })
	}

	const openTarget = article.externalUrl

	const { dialog: deleteDialog, requestDelete } = useArticleDelete({
		id: article.id,
		title: article.title,
		kind: article.kind,
		enableShortcut: true,
	})

	return (
		<div className="flex h-full min-w-0">
			{deleteDialog}
			<div className="relative flex min-w-0 flex-1 flex-col">
				<div className="flex max-h-12 min-h-12 w-full flex-shrink-0 flex-wrap items-center justify-end gap-2 border-b border-gray-200 py-2 pl-[17.5px] pr-3 backdrop-blur-xl dark:border-gray-700/80 dark:bg-gray-900/80">
					<DocsHeaderTabs />
					<div className="mr-auto sm:hidden">
						<MobileBackButton to="/edit-docs" label="Back to docs" />
					</div>
					<div className="flex flex-none items-center gap-2">
						<Button
							variant="outline"
							Icon={OpenExternal}
							disabled={!openTarget}
							onClick={() => {
								if (!openTarget) return
								window.open(openTarget, "_blank", "noopener,noreferrer")
							}}
						>
							Open
						</Button>
						<RoleCheck not={["VIEWER"]}>
							<DropdownMenu
								contentProps={{ side: "bottom", align: "end" }}
								trigger={
									<IconButton
										Icon={MoreVertical}
										variant="ghost"
										aria-label="More actions"
										iconClassName="fill-current"
									/>
								}
							>
								<DropdownMenuItem
									Icon={Trash}
									onClick={requestDelete}
									className="flex w-full"
								>
									<div className="flex w-full items-center justify-between">
										<div>Delete</div>
										<div className="flex gap-1">
											<Shortcut>{ShortcutSymbols.CMD}</Shortcut>
											<Shortcut>{ShortcutSymbols.BACKSPACE}</Shortcut>
										</div>
									</div>
								</DropdownMenuItem>
							</DropdownMenu>
						</RoleCheck>
					</div>
				</div>

				<div className="scroll-container docs-scroller min-h-0 flex-1 overflow-y-auto px-6 pb-20 pt-10">
					<div className="mx-auto flex w-full max-w-[640px] flex-col gap-6">
						<label htmlFor="link-title" className="flex flex-col gap-1.5">
							<span className="text-xs font-medium text-tertiary">Title</span>
							<Input
								id="link-title"
								name="link-title"
								value={title}
								disabled={!canEdit}
								placeholder="Security overview"
								onChange={(e) => {
									setTitle(e.target.value)
									queueSave({ title: e.target.value })
								}}
								onBlur={() => {
									if (timer.current) window.clearTimeout(timer.current)
									void save({ title })
								}}
							/>
							<span className="text-xs text-tertiary">
								What the agent and your team see in the docs tree.
							</span>
						</label>

						<label htmlFor="link-url" className="flex flex-col gap-1.5">
							<span className="text-xs font-medium text-tertiary">URL</span>
							<Input
								id="link-url"
								name="link-url"
								value={url}
								disabled={!canEdit}
								placeholder="https://docs.example.com/security"
								onChange={(e) => setUrl(e.target.value)}
								onBlur={commitUrl}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault()
										commitUrl()
									}
								}}
							/>
							<span className="text-xs text-tertiary">
								The agent reads this exact page when it answers. Other pages on
								the same site need their own link.
							</span>
						</label>

						{article.externalUrl && (
							<LinkPreviewCard url={article.externalUrl} />
						)}
					</div>
				</div>
			</div>
		</div>
	)
}
